import { Number } from "components/common"
import { createPrivilegeObject } from "utils/privileges/privilegesMapper"
import { dateTimeFunction, getTimeDateString, getAgeFromDate } from "utils/date"
import dayjs from "dayjs"

import { t } from "i18next"
import { convertToReadableString, localisedValue } from "utils"
import { CUSTOM_TIME_FORMAT } from "constants"
const detailTransformHelper = response => {
  const lastActivity = dayjs(response?.updatedAt).isValid()
    ? dateTimeFunction({ date: response?.updatedAt, Default: true }).fromNow()
    : "-"
  const profilingObj = {
    ...response?.profilingAttempted,
    industryId: true,
    existingTypeId: true,
    existingLocalityId: true,
    isCompleted: true,
  }
  const profileFilled = response?.profilingAttempted ? !Object.keys(profilingObj).some(e => !profilingObj[e]) : false

  const callBestHour = () => {
    const bestHour = response?.ClientBestTimeToCall?.bestHours?.reduce((max, item) =>
      item?.probability > max?.probability ? item : max
    )?.hour
    if (bestHour != null) {
      const start = dayjs().hour(bestHour).minute(0).second(0)
      const end = start?.add(1, "hour")
      return [start, end]
    }
    return []
  }

  const transformSales = (sales = []) =>
    sales?.map(({ contract, inquiry, ...rest }) => {
      const stages = Object.fromEntries(
        (contract?.contractStages || [])?.map(({ key, stageUpdatedAt }) => [key, stageUpdatedAt])
      )

      const progress = contract?.paymentProgress || {}

      const current =
        inquiry?.verifiedClosedWon !== 0
          ? 5
          : progress.remainingCompleteDownPaymentAmount === 0
            ? 4
            : progress.remainingPartialDownPaymentAmount === 0
              ? 2
              : progress.remainingMinimumTokenAmount === 0
                ? 1
                : null

      return {
        ...rest,
        inquiry,
        contract: { ...contract, contractStages: stages },
        current,
      }
    })

  const transformedData = {
    ...response,
    ClientFavorite: response?.FavoriteClients,
    salesProfile: { title: t("Sales Profile"), data: [] },
    demographicProfile: { title: t("Demographic Profile"), data: [] },
    mortgageProfile: { title: t("Home Finance Assessment"), data: [] },
    affiliateInfo: { title: t("Affiliate Info"), data: [] },
    moreInfo: { title: t("More Information"), data: [] },
    allocationActivity: {
      name: response?.User?.name,
      createdAt: getTimeDateString(response?.createdAt, false, true),
      lastActivity,
    },
    clientEngagement: {
      qualifiedCalls: response?.stats?.qualifiedCalls?.count?.toString(),
      verifiedCalls: response?.stats?.verifiedCalls?.count?.toString(),
      meetingDone: response?.stats?.meetingDone?.count?.toString(),
    },
    activeSales: {
      ...(response?.activeSales?.transactionalSales && {
        transactionalSales: transformSales(response?.activeSales?.transactionalSales),
      }),
      ...(response?.activeSales?.vasSales && {
        vasSales: transformSales(response?.activeSales?.vasSales),
      }),
    },
    privileges: {},
    profileFilled,
    leadIdsWithTasks: !!response.leadIdsWithTasks ? response.leadIdsWithTasks.split(",") : [],
    bestCallHour: `${callBestHour()?.[0]?.format(CUSTOM_TIME_FORMAT)} - ${callBestHour()?.[1]?.format(
      CUSTOM_TIME_FORMAT
    )}`,
    isCurrentHourBest: dayjs().isAfter(callBestHour()?.[0]) && dayjs().isBefore(callBestHour()?.[1]),
  }

  const {
    budget,
    interestedProjects,
    ClientBuyingIntent,
    interestedAreaUnit,
    ClientExistingProperty,
    ClientRating,
    occupation,
    Industry,
    gender,
    dateOfBirth,
    address,
    Country,
    nationalId,
    privileges,
    City,
    Affiliate,
    Nationality,
    children,
    maritalStatus,
    InterestedType,
    preferredFinancing,
    preferredCityRegion,
    PreferredCity,
    PreferredLocality,
    interestedMaxArea,
    intent,
    hasExistingProperty,
    mortgageEligibilityStatus,
    mortgageEligibilityAmount,
    monthlySalary,
    totalLiabilities,
    additionalIncome,
    dependents,
    firstHome,
    propertyType,
    propertyValue,
    downPayment,
    preferredBankName,
    preferredBankProduct,
    occupationOtherText,
  } = response

  transformedData?.salesProfile?.data?.push(
    hasExistingProperty !== null && {
      label: t("Existing Property"),
      data: hasExistingProperty > 0 ? t("Yes") : t("No"),
    },
    budget && {
      label: t("Buying Budget"),
      data: budget,
      isComponent: true,
      type: "numberComponent",
      columnView: true,
    },
    interestedProjects?.length > 0 && {
      label: t("Interested Project"),
      data: interestedProjects.map(e => ({
        taglabel: `${localisedValue({ object: e, key: "name" })}`,
        tagcolor: "#3a77ff",
      })),
    },
    ClientBuyingIntent && {
      label: t("Investment Plan"),
      data: localisedValue({ object: ClientBuyingIntent, key: "displayName" }),
    },
    interestedMaxArea && {
      label: t("Interested Area Size"),
      data: interestedMaxArea,
    },
    interestedAreaUnit && {
      label: t("Interested Area Unit"),
      data: [{ taglabel: localisedValue({ object: response, key: "interestedAreaUnit" }), tagcolor: "#7858a6" }],
    },
    ClientExistingProperty && {
      label: t("Existing Property"),
      data: [
        {
          taglabel:
            localisedValue({ object: ClientExistingProperty.Locality, key: "name" }) ||
            localisedValue({ object: ClientExistingProperty.Type, key: "name" }),
          tagcolor: "#7858a6",
        },
      ],
    },
    intent && {
      label: t("Primary Intent"),
      data: convertToReadableString(localisedValue({ object: response, key: "intent" })),
    },
    InterestedType && {
      label: t("Type of Property"),
      data: localisedValue({ object: InterestedType, key: "name" }),
    },
    preferredFinancing && {
      label: t("Preferred Financing"),
      data: convertToReadableString(localisedValue({ object: response, key: "preferredFinancing" })),
    },
    PreferredCity && {
      label: t("Preferred City"),
      data: localisedValue({ object: PreferredCity, key: "name" }),
    },
    preferredCityRegion && {
      label: t("Preferred Region"),
      data: convertToReadableString(localisedValue({ object: response, key: "preferredCityRegion" })),
    },
    PreferredLocality && {
      label: t("Preferred Location"),
      data: localisedValue({ object: PreferredLocality, key: "name" }),
    },
    response?.profiling?.relationshipStrength && {
      label: response.profiling.relationshipStrength.label,
      data: response.profiling.relationshipStrength.displayValue,
    },
    response?.profiling?.annualRevenueAUM && {
      label: t("Annual Revenue"),
      data: response.profiling.annualRevenueAUM.value,
      isComponent: true,
      type: "numberComponent",
      columnView: true,
    },
    response?.profiling?.investmentStrategy && {
      label: response.profiling.investmentStrategy.label,
      data: response.profiling.investmentStrategy.displayValue,
    },
    response?.profiling?.investmentPeriod && {
      label: response.profiling.investmentPeriod.label,
      data: response.profiling.investmentPeriod.displayValue,
    },
    response?.profiling?.targetAnnualIncomePercent && {
      label: response.profiling.targetAnnualIncomePercent.label,
      data: response.profiling.targetAnnualIncomePercent.displayValue + " %",
    },
    response?.profiling?.targetCapitalReturnPercent && {
      label: response.profiling.targetCapitalReturnPercent.label,
      data: response.profiling.targetCapitalReturnPercent.displayValue + " %",
    },
    response?.profiling?.targetAllocation && {
      label: t("Target Allocation"),
      data: response.profiling.targetAllocation.value,
      isComponent: true,
      type: "numberComponent",
      columnView: true,
    },
    response?.profiling?.capitalStructure && {
      label: response.profiling.capitalStructure.label,
      data: response.profiling.capitalStructure.displayValue,
    },
    response?.profiling?.assetClass && {
      label: response.profiling.assetClass.label,
      data: response.profiling.assetClass.displayValue,
    },
    response?.profiling?.riskProfile && {
      label: response.profiling.riskProfile.label,
      data: response.profiling.riskProfile.displayValue,
    },
    response?.profiling?.realEstateIndustryExposure && {
      label: t("Has exposure to real estate industry?"),
      data: response.profiling.realEstateIndustryExposure.displayValue,
    }
  )

  transformedData?.demographicProfile?.data?.push(
    ClientRating && { label: t("Client Rating"), data: localisedValue({ object: ClientRating, key: "name" }) },
    response?.EmploymentType?.id && {
      label: t("Employment Type"),
      data: localisedValue({ object: response?.EmploymentType, key: "title" }),
    },
    occupation && {
      label: t("Occupation"),
      data:
        occupation === "others" || occupation === "Others"
          ? localisedValue({ object: response, key: "occupationOtherText" })
          : localisedValue({ object: response, key: "occupation" }),
    },
    Industry && { label: t("Industry"), data: localisedValue({ object: Industry, key: "name" }) },
    response?.profiling?.netWorth && {
      label: t("Net Worth"),
      data: response.profiling.netWorth.value,
      isComponent: true,
      type: "numberComponent",
      columnView: true,
    },
    response?.profiling?.politicalExposure && {
      label: t("Is the client politically exposed (PEP)?"),
      data: response.profiling.politicalExposure.displayValue,
    },
    response?.profiling?.marketExposure && {
      key: "marketExposure",
      label: response.profiling.marketExposure.label,
      data: response.profiling.marketExposure.displayValue,
    },
    gender && { label: t("Gender"), data: t(gender) },
    dateOfBirth && { label: t("Age"), data: getAgeFromDate(dateOfBirth) },
    address && { label: t("Address"), data: localisedValue({ object: response, key: "address" }), columnView: true },
    City && { label: t("City"), data: localisedValue({ object: City, key: "name" }) },
    Country && { label: t("Country"), data: localisedValue({ object: Country, key: "name" }) },
    maritalStatus && { label: t("Marital Status"), data: localisedValue({ object: response, key: "maritalStatus" }) },
    children !== null && { label: t("Children"), data: children > 0 ? t("Yes") : t("No") },
    Nationality && { label: t("Nationality"), data: localisedValue({ object: Nationality, key: "name" }) }
  )

  transformedData?.mortgageProfile?.data?.push(
    mortgageEligibilityStatus && { label: t("Eligibility Status"), data: t(mortgageEligibilityStatus) },
    mortgageEligibilityAmount && {
      label: t("Eligibility (Max. Loan)"),
      data: mortgageEligibilityAmount,
      isComponent: true,
      type: "numberComponent",
    },
    monthlySalary && {
      label: t("Monthly Salary (Net)"),
      data: monthlySalary,
      isComponent: true,
      type: "numberComponent",
    },
    totalLiabilities && {
      label: t("Total Monthly Obligations"),
      data: totalLiabilities,
      isComponent: true,
      type: "numberComponent",
    },
    additionalIncome && { label: t("Other sources of income"), data: additionalIncome },
    dependents && { label: t("Dependents"), data: dependents },
    firstHome !== null && { label: t("First Home"), data: firstHome ? t("Yes") : t("No") },
    propertyType && { label: t("Property Type"), data: propertyType },
    propertyValue && { label: t("Property Value"), data: propertyValue },
    downPayment && { label: t("Down Payment"), data: downPayment },
    preferredBankName && { label: t("Preferred Bank Name"), data: preferredBankName },
    preferredBankProduct && { label: t("Preferred Bank Product"), data: preferredBankProduct }
  )

  transformedData?.affiliateInfo?.data?.push(Affiliate && { label: t("MOU ID"), data: Affiliate?.AffiliateMOU?.id })

  transformedData?.moreInfo?.data?.push(
    nationalId && { label: t("CNIC"), data: nationalId },
    dateOfBirth && { label: t("DOB"), data: new Date(dateOfBirth).toLocaleDateString() },
    (response?.companyName ?? response?.profiling?.companyNameProfiling?.displayValue) && {
      label: t("Company name"),
      data: response?.companyName ?? response?.profiling?.companyNameProfiling?.displayValue,
    },
    response?.profiling?.keyShareholderUBO && {
      label: t("Key Shareholder/UBO"),
      data: response.profiling.keyShareholderUBO.displayValue,
    }
  )

  transformedData.privileges = createPrivilegeObject(privileges)
  return transformedData
}

export default detailTransformHelper
